import type { ProviderKind } from "@cmmd-forge/contracts";
import { useState } from "react";
import type { ModelPickerRowModel } from "./ModelListRow";
import { favoriteKey } from "./modelPickerFavorites";
import { scoreModelPickerSearch } from "./modelPickerSearch";

export type PickerModel = ModelPickerRowModel & { readonly provider: ProviderKind };

export function useModelPickerModels(input: {
  readonly options: Record<ProviderKind, ReadonlyArray<ModelPickerRowModel>>;
  readonly providerKinds: ReadonlySet<string>;
  readonly lockedProvider: ProviderKind | null;
  readonly selectedProvider: ProviderKind | "favorites";
  readonly activeProvider: ProviderKind;
  readonly activeModel: string;
  readonly favorites: ReadonlySet<string>;
  readonly search: string;
}) {
  const [expandedBySelection, setExpandedBySelection] = useState<Record<string, boolean>>({});
  const flatModels: PickerModel[] = [];
  for (const provider of Object.keys(input.options) as ProviderKind[]) {
    if (!input.providerKinds.has(provider)) continue;
    for (const option of input.options[provider] ?? []) {
      flatModels.push({ ...option, provider });
    }
  }
  const scope = input.lockedProvider ?? input.selectedProvider;
  const scoped = flatModels.filter((model) =>
    scope === "favorites"
      ? input.favorites.has(favoriteKey(model.provider, model.slug))
      : model.provider === scope,
  );
  const isSearching = input.search.trim().length > 0;
  const selectionKey = `${scope}:${input.activeProvider}:${input.activeModel}`;
  const activeIsLegacy = scoped.some(
    (model) =>
      model.isLegacy && model.provider === input.activeProvider && model.slug === input.activeModel,
  );
  const legacyExpanded = expandedBySelection[selectionKey] ?? activeIsLegacy;
  const current = scoped.filter((model) => !model.isLegacy);
  const legacy = scoped.filter((model) => model.isLegacy);
  const showLegacySection = !isSearching && scope !== "favorites" && legacy.length > 0;
  let visibleModels = showLegacySection ? [...current, ...(legacyExpanded ? legacy : [])] : scoped;
  if (isSearching) {
    visibleModels = scoped
      .map((model) => ({
        model,
        score: scoreModelPickerSearch({ ...model, providerKind: model.provider }, input.search),
        favorite: input.favorites.has(favoriteKey(model.provider, model.slug)),
      }))
      .filter(
        (entry): entry is { model: PickerModel; score: number; favorite: boolean } =>
          entry.score !== null,
      )
      .toSorted(
        (a, b) =>
          a.score - b.score ||
          Number(b.favorite) - Number(a.favorite) ||
          a.model.name.localeCompare(b.model.name),
      )
      .map((entry) => entry.model);
  }
  return {
    flatModels,
    visibleModels,
    showLegacySection,
    legacyStartIndex: current.length,
    legacyCount: legacy.length,
    legacyExpanded,
    toggleLegacy: () =>
      setExpandedBySelection((previous) => ({ ...previous, [selectionKey]: !legacyExpanded })),
  };
}
